/**
 * Core component for rendering CDASHIG screens.
 * @packageDocumentation 
 */
import React, { useState, useEffect, useContext } from 'react';
import Section from '../../components/Section/Section';
import { useParams, Link, useLocation } from 'react-router-dom';
import { getPreferredTab, getCDASHIGLink } from '../../utils/LinkUtils';
import { Stack } from '@fluentui/react/lib/Stack';
import { Label } from '@fluentui/react/lib/Label';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import { PivotKey } from '../../models/PivotKey';
import ClassDomainScenarioSelector from './components/ClassDomainScenarioSelector';
import ClassDomainScenarioTabs from './components/ClassDomainScenarioTabs';
import FieldsTabs from './components/FieldsTabs';
import useCDASHIGData from './hooks/useCDASHIGData';
import useCDASHIGRouteObserver from './hooks/useCDASHIGRouteObserver';
import AppContext from '../../components/AppContext/AppContext';
import StandardExportButton from '../../components/ExportButton/StandardExportButton';

export interface CDASHIGComponentProps {
	getLinkOverride: (standard: string, version: string, classId?: string, domainId?: string, scenarioId?: string, keepCurrentParams?: boolean) => string;
}

const defaultPageTitle = "CDASHIG";

const CDASHIGComponent: React.FunctionComponent<CDASHIGComponentProps> = ({getLinkOverride}) => {
  const location = useLocation();
  const { standard, version, productSubtype, classId, domainId, scenarioId } = useParams<any>();
  const [pageTitle, setPageTitle] = useState<string>(defaultPageTitle);
  const [selectedMetaPivotKey, setSelectedMetaPivotKey] = useState<PivotKey>(PivotKey.Class);
  const [selectedListPivotKey, setSelectedListPivotKey] = useState<PivotKey>(PivotKey.Class);
  const { dataService } = useContext(AppContext);
  const getLinkFunc = getLinkOverride != null ? getLinkOverride : getCDASHIGLink;

  // Fetch Page Title
  useEffect(() => {
    dataService.nav.getNavLinkFromHref(getLinkFunc(standard, version)).then(page => {
      if (page) setPageTitle(page.shortTitle)
    });
  }, [version]);

  // Fetch CDASHIG Data
  const {
    isLoadingItem, isLoadingClass, isLoadingItemImplements, isLoadingSections,
    currentItem, currentClass, currentDomain, currentScenario, currentItemImplements, sections,
  } = useCDASHIGData(standard, version, productSubtype, classId, domainId, scenarioId);

  // Handle CDASHIG Route Changes
  useCDASHIGRouteObserver(standard, version, classId, domainId, scenarioId, currentItem, currentClass, getLinkFunc);

  // Update Selected Tab
  useEffect(() => {
    const preferredTab = getPreferredTab(location);
    if (preferredTab) {
      setSelectedMetaPivotKey(preferredTab);
      setSelectedListPivotKey(preferredTab);
    }
    else if (currentScenario) {
      setSelectedMetaPivotKey(PivotKey.Scenario);
      setSelectedListPivotKey(PivotKey.Scenario);
    }
    else if (currentDomain) {
      setSelectedMetaPivotKey(PivotKey.Domain);
      setSelectedListPivotKey(PivotKey.Domain);
    }
    else {
      setSelectedMetaPivotKey(PivotKey.Class);
      setSelectedListPivotKey(PivotKey.Class);
    }
  }, [currentClass, currentDomain, currentScenario]);

  return (
    <>
      <Section pageTitle={pageTitle}>
        {!isLoadingItem && !isLoadingItemImplements && currentItem
          ? <>
            <Stack horizontal horizontalAlign='space-between'>
              <Stack horizontal tokens={{ childrenGap: 20 }}>
                <Stack>
                  <Label>Status</Label>
                  <span className={currentItem.registrationStatus === "Draft" ? "draft-status" : ""}>{currentItem.registrationStatus}</span>
                </Stack>
                <Stack>
                  <Label>Effective Date</Label>
                  <span>{currentItem.effectiveDate}</span>
                </Stack>
                {currentItemImplements && (
                  <Stack>
                    <Label>Implements</Label>
                    <span>
                      <Link to={currentItemImplements.href}>
                        {currentItemImplements.shortTitle}
                      </Link>
                    </span>
                  </Stack>
                )}
              </Stack>
              <StandardExportButton
                pageTitle={pageTitle}
                standard={standard}
                version={version}
                productSubtype={productSubtype}
              />
            </Stack>
          </>
          : <>
            {[1, 2].map(idx => (
              <Shimmer key={idx} styles={{ root: { minHeight: 17 } }} shimmerElements={[
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: 20 },
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: 20 },
                { type: ShimmerElementType.line, width: 50, height: 8 },
                { type: ShimmerElementType.gap, width: '100%' },
              ]} />
            ))}
          </>
        }
      </Section>

      <ClassDomainScenarioSelector
        classId={classId}
        domainId={domainId}
        scenarioId={scenarioId}
        classes={currentItem ? currentItem.classes : null}
        domains={currentClass ? currentClass.domains : null}
        scenarios={currentDomain ? currentDomain._links.scenarios : null}
        isLoadingClass={isLoadingClass}
        isLoadingItem={isLoadingItem}
        standard={standard}
        version={version}
        getLink={getLinkFunc}
      />

      {undefined !== isLoadingClass && (
        <ClassDomainScenarioTabs
          currentClass={currentClass}
          currentDomain={currentDomain}
          currentScenario={currentScenario}
          isLoadingClass={isLoadingClass}
          selectedPivotKey={selectedMetaPivotKey}
          setSelectedPivotKey={setSelectedMetaPivotKey}
          isLoadingSections={isLoadingSections}
          sections={sections}
          standardType={standard}
          version={version}
          standardSubtype={productSubtype}
        />
      )}

      {undefined !== isLoadingClass && (
        <FieldsTabs
          isIG={true}
          currentClass={currentClass}
          currentDomain={currentDomain}
          currentScenario={currentScenario}
          isLoadingClass={isLoadingClass}
          selectedPivotKey={selectedListPivotKey}
          setSelectedPivotKey={setSelectedListPivotKey}
        />
      )}
    </>
  );
}

export default CDASHIGComponent;
